/**
 * Canonical pagination cursor date is a timezone-less ISO-8601 LocalDateTime string.
 * Legacy Jackson numeric arrays and comma strings are normalized for compatibility only.
 */

const ISO_LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?$/;

function isIntegerString(value) {
  return typeof value === 'string' && /^-?\d+$/.test(value);
}

function asInt(value) {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (isIntegerString(value)) return Number(value);
  return null;
}

function pad(value, width) {
  return String(value).padStart(width, '0');
}

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year, month) {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return lengths[month - 1];
}

function fromParts(parts) {
  if (!Array.isArray(parts) || (parts.length !== 6 && parts.length !== 7)) return null;
  const year = asInt(parts[0]);
  const month = asInt(parts[1]);
  const day = asInt(parts[2]);
  const hour = asInt(parts[3]);
  const minute = asInt(parts[4]);
  const second = asInt(parts[5]);
  const nano = parts.length === 7 ? asInt(parts[6]) : 0;
  if ([year, month, day, hour, minute, second, nano].some((part) => part == null)) return null;
  if (year < 1 || year > 9999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;
  if (second < 0 || second > 59) return null;
  if (nano < 0 || nano > 999999999) return null;

  let iso = `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}T${pad(hour, 2)}:${pad(minute, 2)}:${pad(second, 2)}`;
  if (nano > 0) {
    iso += `.${String(nano).padStart(9, '0').replace(/0+$/, '')}`;
  }
  return iso;
}

export function normalizeCursorDateTime(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return fromParts(value);
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed || trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed)) return null;
  if (ISO_LOCAL_DATE_TIME.test(trimmed)) return trimmed;
  if (trimmed.includes(',')) return fromParts(trimmed.split(',').map((part) => part.trim()));
  return null;
}
